import React, { useState, useEffect } from 'react';
import { Link, useNavigate, Routes, Route } from 'react-router-dom';
import styled from 'styled-components';
import { PageLayout, Container } from '../../components/Layout';
import { Card, CardBody, CardHeader, Button, Badge, StatusBadge, Table, EmptyState, PageLoader, FormGroup, Label, Input, Select, Textarea, Stars, StatCard, Modal } from '../../components/UI';
import { useAuth } from '../../context/AuthContext';
import { useCart } from '../../context/CartContext';
import api, { fmt, imgUrl } from '../../utils/api';
import toast from 'react-hot-toast';
// PayPal loaded via CDN script tag

const PAYPAL_CLIENT_ID = process.env.REACT_APP_PAYPAL_CLIENT_ID || 'sb';

/* ─── ORDERS PAGE ─────────────────────────────────────────── */
export function Orders() {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    api.get('/orders/my').then(d => setOrders(d.orders || [])).finally(() => setLoading(false));
  }, []);

  const STEPS = ['pending','confirmed','packed','shipped','out_for_delivery','delivered'];

  if (loading) return <PageLayout><PageLoader /></PageLayout>;

  return (
    <PageLayout>
      <Container style={{ padding:'40px 20px' }}>
        <h2 style={{ marginBottom:24 }}>My Orders</h2>
        {!orders.length
          ? <EmptyState icon="📦" title="No orders yet" subtitle="Start shopping!" action={<Link to="/shop"><Button>Browse Products</Button></Link>} />
          : (
            <div style={{ display:'flex', flexDirection:'column', gap:16 }}>
              {orders.map(o => (
                <Card key={o.id}>
                  <CardBody>
                    <div style={{ display:'flex', justifyContent:'space-between', flexWrap:'wrap', gap:12, marginBottom:16 }}>
                      <div>
                        <div style={{ fontWeight:700, fontSize:'1rem' }}>#{o.order_number}</div>
                        <div style={{ fontSize:'0.82rem', color:'var(--gray-500)', marginTop:2 }}>{fmt.datetime(o.created_at)}</div>
                      </div>
                      <div style={{ display:'flex', alignItems:'center', gap:12 }}>
                        <StatusBadge status={o.order_status} />
                        <StatusBadge status={o.payment_status} />
                        <span style={{ fontWeight:700, color:'var(--primary)' }}>{fmt.currency(o.total)}</span>
                      </div>
                    </div>

                    {/* Tracking timeline */}
                    <div style={{ display:'flex', justifyContent:'space-between', position:'relative', marginBottom:16, overflowX:'auto' }}>
                      <div style={{ position:'absolute', top:12, left:20, right:20, height:2, background:'var(--gray-200)', zIndex:0 }} />
                      {STEPS.map((step, i) => {
                        const done = STEPS.indexOf(o.order_status) >= i;
                        const curr = o.order_status === step;
                        return (
                          <div key={step} style={{ display:'flex', flexDirection:'column', alignItems:'center', gap:6, flex:1, position:'relative', zIndex:1, minWidth:60 }}>
                            <div style={{ width:24, height:24, borderRadius:'50%', background: done ? 'var(--primary)' : '#fff', border:`2px solid ${done ? 'var(--primary)' : 'var(--gray-300)'}`, display:'flex', alignItems:'center', justifyContent:'center', color:'#fff', fontSize:'0.7rem', boxShadow: curr ? '0 0 0 4px var(--primary-100)' : 'none' }}>
                              {done ? '✓' : i+1}
                            </div>
                            <span style={{ fontSize:'0.68rem', color: done ? 'var(--primary-dark)' : 'var(--gray-400)', textAlign:'center', fontWeight: curr ? 700 : 400, lineHeight:1.2 }}>
                              {fmt.orderStatus(step)}
                            </span>
                          </div>
                        );
                      })}
                    </div>

                    <Button $variant="outline" $size="sm" onClick={() => setSelectedOrder(o)}>View Details</Button>
                  </CardBody>
                </Card>
              ))}
            </div>
          )
        }
      </Container>

      {/* Order detail modal */}
      <Modal open={!!selectedOrder} onClose={() => setSelectedOrder(null)} title={`Order #${selectedOrder?.order_number}`} width="680px">
        {selectedOrder && <OrderDetail orderId={selectedOrder.id} />}
      </Modal>
    </PageLayout>
  );
}

function OrderDetail({ orderId }) {
  const [order, setOrder] = useState(null);
  useEffect(() => { api.get(`/orders/my/${orderId}`).then(d => setOrder(d.order)).catch(()=>{}); }, [orderId]);
  if (!order) return <PageLoader />;
  return (
    <div>
      <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:12, marginBottom:20 }}>
        {[['Status', <StatusBadge status={order.order_status} />],['Payment', <StatusBadge status={order.payment_status} />],['Total', fmt.currency(order.total)],['Items', order.items?.length]].map(([l,v]) => (
          <div key={l} style={{ background:'var(--gray-50)', padding:12, borderRadius:'var(--radius)', fontSize:'0.88rem' }}>
            <div style={{ color:'var(--gray-500)', marginBottom:4 }}>{l}</div>
            <div style={{ fontWeight:600 }}>{v}</div>
          </div>
        ))}
      </div>

      <h4 style={{ marginBottom:12 }}>Items</h4>
      <div style={{ display:'flex', flexDirection:'column', gap:8, marginBottom:20 }}>
        {(order.items || []).map(i => (
          <div key={i.id} style={{ display:'flex', gap:12, padding:10, background:'var(--gray-50)', borderRadius:'var(--radius)' }}>
            <div style={{ flex:1, fontSize:'0.88rem' }}>
              <div style={{ fontWeight:500 }}>{i.product_name}</div>
              {i.color && <div style={{ color:'var(--gray-500)', fontSize:'0.8rem' }}>{i.color}{i.size ? ` / ${i.size}` : ''}</div>}
            </div>
            <div style={{ fontSize:'0.88rem' }}>{i.quantity} × {fmt.currency(i.price)}</div>
            <div style={{ fontWeight:700, color:'var(--primary)' }}>{fmt.currency(i.total)}</div>
          </div>
        ))}
      </div>

      <h4 style={{ marginBottom:12 }}>Tracking History</h4>
      <div style={{ borderLeft:'2px solid var(--gray-200)', paddingLeft:16, display:'flex', flexDirection:'column', gap:12 }}>
        {(order.tracking || []).map(t => (
          <div key={t.id} style={{ position:'relative' }}>
            <div style={{ position:'absolute', left:-21, top:4, width:8, height:8, borderRadius:'50%', background:'var(--primary)' }} />
            <div style={{ fontWeight:600, fontSize:'0.88rem' }}>{fmt.orderStatus(t.status)}</div>
            {t.courier_name && <div style={{ fontSize:'0.8rem', color:'var(--gray-600)' }}>{t.courier_name}{t.tracking_number ? ` — ${t.tracking_number}` : ''}</div>}
            {t.notes && <div style={{ fontSize:'0.8rem', color:'var(--gray-500)' }}>{t.notes}</div>}
            <div style={{ fontSize:'0.75rem', color:'var(--gray-400)' }}>{fmt.datetime(t.created_at)}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

/* ─── CHECKOUT PAGE ───────────────────────────────────────── */
export function Checkout() {
  const { items, total, clear } = useCart();
  const { isLoggedIn } = useAuth();
  const navigate = useNavigate();
  const [addresses, setAddresses] = useState([]);
  const [selectedAddr, setSelectedAddr] = useState(null);
  const [coupon, setCoupon] = useState('');
  const [couponData, setCouponData] = useState(null);
  const [notes, setNotes] = useState('');
  const [orderId, setOrderId] = useState(null);
  const [showPayPal, setShowPayPal] = useState(false);
  const [addAddrOpen, setAddAddrOpen] = useState(false);
  const [newAddr, setNewAddr] = useState({ label:'Home', full_name:'', phone:'', address_line1:'', city:'', state:'', zip:'', country:'India' });

  useEffect(() => {
    if (!isLoggedIn) { navigate('/login'); return; }
    api.get('/auth/addresses').then(d => { setAddresses(d.addresses || []); if (d.addresses?.length) setSelectedAddr(d.addresses.find(a=>a.is_default) || d.addresses[0]); });
  }, [isLoggedIn, navigate]);

  const applyDiscount = couponData ? (couponData.type === 'percentage' ? total * couponData.value / 100 : couponData.value) : 0;
  const finalTotal = Math.max(0, total - applyDiscount);

  const validateCoupon = async () => {
    try {
      const d = await api.post('/coupons/validate', { code: coupon, subtotal: total });
      setCouponData(d.coupon);
      toast.success(`Coupon applied! -${fmt.currency(d.discount)} off`);
    } catch (e) { toast.error(e.message); }
  };

  const placeOrder = async () => {
    if (!selectedAddr) { toast.error('Please select a delivery address'); return; }
    if (!items.length) { toast.error('Your cart is empty'); return; }
    try {
      const d = await api.post('/orders', {
        address_id: selectedAddr.id,
        items: items.map(i => ({ product_id: i.product_id, variant_id: i.variant_id || null, quantity: i.quantity })),
        coupon_code: couponData ? coupon : undefined,
        notes, payment_method: 'paypal'
      });
      setOrderId(d.order_id);
      setShowPayPal(true);
    } catch (e) { toast.error(e.message); }
  };

  const handlePayPalApprove = async (data, actions) => {
    try {
      await api.post('/payments/paypal/capture', { paypal_order_id: data.orderID, order_id: orderId });
      await clear();
      toast.success('Payment successful! Order confirmed.');
      navigate('/account/orders');
    } catch { toast.error('Payment capture failed'); }
  };

  const saveAddress = async () => {
    try {
      await api.post('/auth/addresses', newAddr);
      const d = await api.get('/auth/addresses');
      setAddresses(d.addresses || []);
      setAddAddrOpen(false);
      toast.success('Address saved');
    } catch (e) { toast.error(e.message); }
  };

  if (!items.length) return (
    <PageLayout>
      <Container style={{ padding:'60px 20px', textAlign:'center' }}>
        <EmptyState icon="🛒" title="Your cart is empty" action={<Link to="/shop"><Button>Start Shopping</Button></Link>} />
      </Container>
    </PageLayout>
  );

  return (
    <PageLayout>
      <Container style={{ padding:'40px 20px' }}>
        <h2 style={{ marginBottom:28 }}>Checkout</h2>
        <div style={{ display:'grid', gridTemplateColumns:'1fr 380px', gap:28, alignItems:'start' }}>
          {/* Left */}
          <div style={{ display:'flex', flexDirection:'column', gap:20 }}>
            {/* Delivery Address */}
            <Card>
              <CardHeader>
                <h3>Delivery Address</h3>
                <Button $variant="outline" $size="sm" onClick={() => setAddAddrOpen(true)}>+ Add New</Button>
              </CardHeader>
              <CardBody>
                {!addresses.length
                  ? <p style={{ color:'var(--gray-500)' }}>No addresses. Add one to continue.</p>
                  : <div style={{ display:'flex', flexDirection:'column', gap:10 }}>
                      {addresses.map(a => (
                        <label key={a.id} style={{ display:'flex', gap:12, padding:14, borderRadius:'var(--radius)', border:`1.5px solid ${selectedAddr?.id===a.id?'var(--primary)':'var(--gray-200)'}`, cursor:'pointer', background: selectedAddr?.id===a.id?'var(--primary-50)':'#fff' }}>
                          <input type="radio" name="addr" checked={selectedAddr?.id===a.id} onChange={() => setSelectedAddr(a)} style={{ accentColor:'var(--primary)', marginTop:2 }} />
                          <div style={{ fontSize:'0.88rem' }}>
                            <div style={{ fontWeight:600 }}>{a.full_name} · {a.label}</div>
                            <div style={{ color:'var(--gray-600)' }}>{a.address_line1}, {a.city}, {a.state} {a.zip}</div>
                            <div style={{ color:'var(--gray-500)' }}>{a.phone}</div>
                          </div>
                        </label>
                      ))}
                    </div>
                }
              </CardBody>
            </Card>

            {/* Coupon */}
            <Card>
              <CardHeader><h3>Coupon Code</h3></CardHeader>
              <CardBody>
                <div style={{ display:'flex', gap:8 }}>
                  <Input placeholder="Enter coupon code" value={coupon} onChange={e => setCoupon(e.target.value.toUpperCase())} />
                  <Button $variant="outline" onClick={validateCoupon}>Apply</Button>
                </div>
                {couponData && <p style={{ color:'var(--success)', fontSize:'0.85rem', marginTop:8 }}>✓ Coupon applied!</p>}
              </CardBody>
            </Card>

            {/* Notes */}
            <Card>
              <CardHeader><h3>Order Notes (Optional)</h3></CardHeader>
              <CardBody>
                <Textarea placeholder="Any special instructions..." value={notes} onChange={e => setNotes(e.target.value)} style={{ minHeight:80 }} />
              </CardBody>
            </Card>
          </div>

          {/* Right - Order Summary */}
          <div>
            <Card>
              <CardHeader><h3>Order Summary</h3></CardHeader>
              <CardBody>
                <div style={{ display:'flex', flexDirection:'column', gap:10, marginBottom:16 }}>
                  {items.map(i => (
                    <div key={i.id} style={{ display:'flex', gap:10, fontSize:'0.88rem' }}>
                      <img src={imgUrl(i.image)} alt="" style={{ width:44, height:44, borderRadius:'var(--radius-sm)', objectFit:'cover', background:'var(--gray-100)' }} />
                      <div style={{ flex:1 }}>
                        <div style={{ fontWeight:500 }}>{i.name || i.product_name}</div>
                        {i.color && <div style={{ color:'var(--gray-500)', fontSize:'0.78rem' }}>{i.color}{i.size ? ` / ${i.size}` : ''}</div>}
                      </div>
                      <div style={{ fontWeight:600 }}>{fmt.currency((i.sale_price || i.price) * i.quantity)}</div>
                    </div>
                  ))}
                </div>
                <div style={{ borderTop:'1px solid var(--gray-100)', paddingTop:12, display:'flex', flexDirection:'column', gap:8, fontSize:'0.9rem' }}>
                  <div style={{ display:'flex', justifyContent:'space-between' }}><span>Subtotal</span><span>{fmt.currency(total)}</span></div>
                  {couponData && <div style={{ display:'flex', justifyContent:'space-between', color:'var(--success)' }}><span>Discount</span><span>-{fmt.currency(applyDiscount)}</span></div>}
                  <div style={{ display:'flex', justifyContent:'space-between' }}><span>Shipping</span><span style={{ color:'var(--success)' }}>Calculated at server</span></div>
                  <div style={{ display:'flex', justifyContent:'space-between', fontWeight:700, fontSize:'1.05rem', paddingTop:8, borderTop:'1px solid var(--gray-100)' }}>
                    <span>Total</span><span style={{ color:'var(--primary)' }}>{fmt.currency(finalTotal)}</span>
                  </div>
                </div>

                {!showPayPal
                  ? <Button $block $size="lg" onClick={placeOrder} style={{ marginTop:16 }}>Proceed to Payment</Button>
                  : (
                    <div style={{ marginTop:16 }} id="paypal-button-container">
                      <div id="paypal-buttons-root" data-order-id={orderId} data-client-id={PAYPAL_CLIENT_ID}></div>
                      <script dangerouslySetInnerHTML={{__html: `
                        if(window.paypal) {
                          paypal.Buttons({
                            createOrder: async () => {
                              const r = await fetch('/api/payments/paypal/create', {method:'POST',headers:{'Content-Type':'application/json','Authorization':'Bearer '+localStorage.getItem('amv_token')},body:JSON.stringify({order_id:${orderId}})});
                              const d = await r.json(); return d.paypal_order_id;
                            },
                            onApprove: async (data) => {
                              await fetch('/api/payments/paypal/capture', {method:'POST',headers:{'Content-Type':'application/json','Authorization':'Bearer '+localStorage.getItem('amv_token')},body:JSON.stringify({paypal_order_id:data.orderID,order_id:${orderId}})});
                              window.location.href='/account/orders';
                            }
                          }).render('#paypal-buttons-root');
                        }
                      `}} />
                    </div>
                  )
                }
              </CardBody>
            </Card>
          </div>
        </div>
      </Container>

      {/* Add Address Modal */}
      <Modal open={addAddrOpen} onClose={() => setAddAddrOpen(false)} title="Add New Address"
        footer={<><Button $variant="secondary" onClick={() => setAddAddrOpen(false)}>Cancel</Button><Button onClick={saveAddress}>Save Address</Button></>}>
        <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:14 }}>
          {[['full_name','Full Name'],['phone','Phone'],['address_line1','Address'],['city','City'],['state','State'],['zip','ZIP Code'],['country','Country']].map(([k,l]) => (
            <FormGroup key={k} style={{ gridColumn: k==='address_line1'?'1/-1':'auto' }}>
              <Label>{l}</Label>
              <Input value={newAddr[k]} onChange={e => setNewAddr(a=>({...a,[k]:e.target.value}))} />
            </FormGroup>
          ))}
        </div>
      </Modal>
    </PageLayout>
  );
}

/* ─── PROFILE PAGE ────────────────────────────────────────── */
export function Profile() {
  const { user, login } = useAuth();
  const [profile, setProfile] = useState({ name: user?.name || '', phone: '' });
  const [passwords, setPasswords] = useState({ current_password:'', new_password:'' });
  const [saving, setSaving] = useState(false);

  const saveProfile = async () => {
    setSaving(true);
    try { await api.put('/auth/profile', profile); toast.success('Profile updated'); }
    catch (e) { toast.error(e.message); }
    finally { setSaving(false); }
  };

  const changePassword = async () => {
    setSaving(true);
    try { await api.put('/auth/change-password', passwords); toast.success('Password changed!'); setPasswords({ current_password:'', new_password:'' }); }
    catch (e) { toast.error(e.message); }
    finally { setSaving(false); }
  };

  return (
    <PageLayout>
      <Container style={{ padding:'40px 20px', maxWidth:700 }}>
        <h2 style={{ marginBottom:28 }}>My Profile</h2>
        <Card style={{ marginBottom:20 }}>
          <CardHeader><h3>Personal Information</h3></CardHeader>
          <CardBody>
            <FormGroup><Label>Full Name</Label><Input value={profile.name} onChange={e => setProfile(p=>({...p,name:e.target.value}))} /></FormGroup>
            <FormGroup><Label>Email</Label><Input value={user?.email} disabled style={{ background:'var(--gray-50)', cursor:'not-allowed' }} /></FormGroup>
            <FormGroup><Label>Phone</Label><Input value={profile.phone} onChange={e => setProfile(p=>({...p,phone:e.target.value}))} /></FormGroup>
            <Button onClick={saveProfile} disabled={saving}>{saving ? 'Saving...' : 'Save Changes'}</Button>
          </CardBody>
        </Card>
        <Card>
          <CardHeader><h3>Change Password</h3></CardHeader>
          <CardBody>
            <FormGroup><Label>Current Password</Label><Input type="password" value={passwords.current_password} onChange={e => setPasswords(p=>({...p,current_password:e.target.value}))} /></FormGroup>
            <FormGroup><Label>New Password</Label><Input type="password" value={passwords.new_password} onChange={e => setPasswords(p=>({...p,new_password:e.target.value}))} /></FormGroup>
            <Button onClick={changePassword} disabled={saving}>{saving ? 'Updating...' : 'Change Password'}</Button>
          </CardBody>
        </Card>
      </Container>
    </PageLayout>
  );
}

/* ─── WISHLIST PAGE ───────────────────────────────────────── */
export function Wishlist() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const { add } = useCart();

  const load = () => api.get('/wishlist').then(d => setItems(d.items || [])).finally(() => setLoading(false));
  useEffect(() => { load(); }, []);

  const removeItem = async (product_id) => {
    await api.delete(`/wishlist/${product_id}`);
    setItems(prev => prev.filter(i => i.product_id !== product_id));
    toast.success('Removed from wishlist');
  };

  return (
    <PageLayout>
      <Container style={{ padding:'40px 20px' }}>
        <h2 style={{ marginBottom:24 }}>My Wishlist</h2>
        {loading ? <PageLoader /> : !items.length
          ? <EmptyState icon="♡" title="Wishlist is empty" action={<Link to="/shop"><Button>Browse Products</Button></Link>} />
          : (
            <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill,minmax(240px,1fr))', gap:20 }}>
              {items.map(item => (
                <Card key={item.id}>
                  <Link to={`/product/${item.slug}`}>
                    <img src={imgUrl(item.image)} alt={item.name} style={{ width:'100%', aspectRatio:'4/3', objectFit:'cover', background:'var(--gray-100)' }} />
                  </Link>
                  <CardBody>
                    <Link to={`/product/${item.slug}`}><div style={{ fontFamily:'var(--font-display)', fontWeight:600, marginBottom:8 }}>{item.name}</div></Link>
                    <div style={{ fontWeight:700, color:'var(--primary)', marginBottom:14 }}>{fmt.currency(item.sale_price || item.price)}</div>
                    <div style={{ display:'flex', gap:8 }}>
                      <Button $block $size="sm" onClick={() => add(item.product_id,null,1,{name:item.name,price:item.price,sale_price:item.sale_price,image:item.image})}>Add to Cart</Button>
                      <Button $variant="ghost" $size="sm" onClick={() => removeItem(item.product_id)}>✕</Button>
                    </div>
                  </CardBody>
                </Card>
              ))}
            </div>
          )
        }
      </Container>
    </PageLayout>
  );
}
